import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DetailedStatisticsCard from "examples/Cards/StatisticsCards/DetailedStatisticsCard";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import PieChart from "examples/Charts/PieChart";
import { useState, useEffect } from "react";
import { getDashboardStats } from "services/dashboardService";
import { CircularProgress } from "@mui/material";

function Default() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getDashboardStats();
        if (response.success) {
          setDashboardData(response.data);
        } else {
          setError("Failed to fetch dashboard stats");
        }
      } catch (err) {
        setError("Error loading dashboard data");
        console.error("Error fetching dashboard stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Transform top categories data for pie chart
  const topActivities =
    dashboardData?.topCategories?.map((category, index) => ({
      _id: index.toString(),
      name: category.categoryName,
      percent: category.percentage,
    })) || [];

  const pieChartData = {
    labels: topActivities.map((a) => a.name),
    datasets: [
      {
        data: topActivities.map((a) => a.percent),
      },
    ],
  };

  // Transform monthly stats data for line chart
  const transformMonthlyStats = (monthlyStats) => {
    if (!monthlyStats || !Array.isArray(monthlyStats)) {
      return {
        labels: [],
        datasets: [
          {
            label: "Activities",
            data: [],
            borderColor: "#3B98D1",
            backgroundColor: "rgba(59, 152, 209, 0.1)",
            tension: 0.4,
            pointBackgroundColor: "#3B98D1",
          },
        ],
      };
    }

    const monthLabels = [];
    const data = [];

    monthlyStats.forEach((monthData) => {
      const month = Object.keys(monthData)[0];
      const value = monthData[month];

      // Convert month name to proper format (e.g., "february" to "Feb")
      const monthName = month.charAt(0).toUpperCase() + month.slice(1, 3);
      monthLabels.push(monthName);
      data.push(value);
    });

    return {
      labels: monthLabels,
      datasets: [
        {
          label: "Activities",
          data: data,
          borderColor: "#3B98D1",
          backgroundColor: "rgba(59, 152, 209, 0.1)",
          tension: 0.4,
          pointBackgroundColor: "#3B98D1",
        },
      ],
    };
  };

  const activitiesData = transformMonthlyStats(dashboardData?.monthlyStats);

  // Users by Age data (keeping this as is since it's not in the API response)
  // const usersByAgeData = {
  //   labels: ["16-20", "21-25", "26-30", "31-36", "36-42", "42+"],
  //   datasets: [
  //     {
  //       label: "Users",
  //       data: [15, 22, 17, 32, 23, 18],
  //       backgroundColor: "#172b4d",
  //       borderRadius: 4,
  //       barThickness: 30,
  //     },
  //   ],
  // };

  // Helper function to format percentage change
  const formatPercentageChange = (change, includePercent = true) => {
    if (change === 0) return includePercent ? "0%" : "0";
    const sign = change > 0 ? "+" : "";
    const value = `${sign}${change}`;
    return includePercent ? `${value}%` : value;
  };

  if (loading) {
    return (
      <ArgonBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </ArgonBox>
    );
  }

  if (error) {
    return (
      <ArgonBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <ArgonTypography color="error">{error}</ArgonTypography>
      </ArgonBox>
    );
  }

  return (
    <>
      <ArgonBox py={3}>
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={4}>
            <DetailedStatisticsCard
              title="Total Activities"
              count={
                dashboardData?.activities?.totalActivities?.toString() || "0"
              }
              percentage={{
                count: formatPercentageChange(
                  dashboardData?.activities?.percentageChange || 0,
                  true
                ),
                text: "since last month",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <DetailedStatisticsCard
              title="Total Regular Users"
              count={dashboardData?.users?.regularUsers?.toString() || "0"}
              percentage={{
                count: formatPercentageChange(
                  dashboardData?.users?.regularUsersSinceLastMonth || 0,
                  false
                ),
                text: "since last month",
              }}
              noPercent
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <DetailedStatisticsCard
              title="Total Business Users"
              count={dashboardData?.users?.businessUsers?.toString() || "0"}
              percentage={{
                count: formatPercentageChange(
                  dashboardData?.users?.businessUsersSinceLastMonth || 0,
                  false
                ),
                text: "since last month",
              }}
              noPercent
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} mb={3} alignItems="stretch">
          <Grid item xs={12} md={6} lg={5} style={{ display: "flex" }}>
            <Card style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <CardContent style={{ flex: 1 }}>
                <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                  Top Categories
                </ArgonTypography>
                {pieChartData?.labels?.length > 0 ? (
                  <PieChart chart={pieChartData} height="100%" />
                ) : (
                  <ArgonBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                      No data available
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={7} style={{ display: "flex" }}>
            <Card style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <CardContent style={{ flex: 1 }}>
                <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                  Activities
                </ArgonTypography>
                {activitiesData?.labels?.length > 0 ? (
                  <GradientLineChart chart={activitiesData} />
                ) : (
                  <ArgonBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <ArgonTypography variant="h6" fontWeight="bold" mb={2}>
                      No data available
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <HorizontalBarChart
              title="Users By Age"
              data={usersByAgeData}
              height={260}
            />
          </Grid>
        </Grid> */}
      </ArgonBox>
    </>
  );
}

export default Default;
